'use client';

import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';
import type { BaseModalProps } from '@/types/modal';

import { SpinnerLoader } from '../Loading';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

// Visually hidden helper for accessibility-only titles.
function VisuallyHidden({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0"
      style={{
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        margin: '-1px',
      }}
    >
      {children}
    </span>
  );
}

type ExtendedBaseModalProps = BaseModalProps & {
  size?: 'default' | 'lg';
  // Optional slot rendered above the title (e.g. AlertModal's tonal icon chip).
  headerLeading?: React.ReactNode;
  // Destructive variant — colours the title with the error tone per Modal spec.
  danger?: boolean;
};

// Canonical footer layout per Modal contract — right-aligned action cluster,
// reverses on mobile, fixed 8px gap regardless of breakpoint.
const FOOTER_CLASS
  = 'flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2';

function BaseModal(props: ExtendedBaseModalProps) {
  const {
    onOpenChange,
    open,
    defaultOpen,
    title,
    description,
    children,
    footer,
    className,
    fullscreen = false,
    isLoadingContent = false,
    size = 'default',
    headerLeading,
    danger = false,
    stickyFooter = false,
  } = props;

  const translate = useTranslations();

  const renderFooter = () => {
    if (footer === undefined) {
      return null;
    }
    if (footer === null) {
      return (
        <DialogFooter className={FOOTER_CLASS} sticky={stickyFooter}>
          <DialogClose asChild>
            <Button
              onClick={() => {
                onOpenChange(false);
              }}
              type="button"
              variant="text"
            >
              {translate('modal.close')}
            </Button>
          </DialogClose>
        </DialogFooter>
      );
    }
    return <DialogFooter className={FOOTER_CLASS} sticky={stickyFooter}>{footer}</DialogFooter>;
  };

  return (
    <Dialog defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size={size === 'lg' ? 'lg' : 'md'}
        className={cn(
          fullscreen === true
            ? 'h-screen w-screen max-w-none flex flex-col rounded-none'
            : 'flex flex-col max-h-[90vh] min-h-min',
          className,
        )}
      >
        {/* When there is no visible title, the header band would collapse to
            an empty padded strip — render only the a11y title in that case. */}
        {title === undefined
          ? (
              <VisuallyHidden>
                <DialogTitle>{translate('modal.modalTitle')}</DialogTitle>
              </VisuallyHidden>
            )
          : (
              <DialogHeader>
                {headerLeading !== undefined && headerLeading}
                <DialogTitle
                  className={cn(
                    danger === true ? 'text-error' : 'text-foreground',
                  )}
                >
                  {title}
                </DialogTitle>
                {description !== undefined && (
                  <DialogDescription>{description}</DialogDescription>
                )}
              </DialogHeader>
            )}
        {/* Body owns its own breathing room (px-6) so it never sits flush
            against the header or footer chrome. With no header band it also
            picks up top padding so content clears the close affordance. */}
        <div
          className={cn(
            'flex flex-1 flex-col gap-4 overflow-y-auto px-6 pb-2',
            title === undefined ? 'pt-5' : 'pt-1',
          )}
        >
          {isLoadingContent === true && (
            <div className="my-6 flex w-full items-center justify-center">
              <SpinnerLoader size="xl" className="text-primary" />
            </div>
          )}
          {isLoadingContent === false && children}
        </div>
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
}

export default BaseModal;
